import * as Yup from 'yup';
import allForm from '../../stateless/Form';
import { Box, Button, CircularProgress } from '@mui/material';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useEditStockQuantity } from '../../Query/StockQuery';
import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { StockAtom } from './StockList';
import { useTranslation } from 'react-i18next';

interface FaqFormValues {
  id?: string;
  quantity: number;
  operation: 'add' | 'subtract' | 'replace';
}

const validationSchema = (t: any) => Yup.object()
  .shape({
    quantity: Yup.string()
      .matches(/^[0-9]+$/, t('stockManagement.form.validation.onlyNumbersAllowed'))
      .max(1000000, t('stockManagement.form.validation.addQuantityMaxDigits')),
    substractQuantity: Yup.string()
      .matches(/^[0-9]+$/, t('stockManagement.form.validation.onlyNumbersAllowed'))
      .max(1000000, t('stockManagement.form.validation.subtractQuantityMaxDigits')),
    replaceQuantity: Yup.string()
      .matches(/^[0-9]+$/, t('stockManagement.form.validation.onlyNumbersAllowed'))
      .max(1000000, t('stockManagement.form.validation.replaceQuantityMaxDigits')),
  })
  .test(
    'at-least-one-field',
    t('stockManagement.form.validation.fillAtLeastOneField'),
    function (value) {
      const { quantity, substractQuantity, replaceQuantity } = value;
      if (!quantity && !substractQuantity && !replaceQuantity) {
        return this.createError({
          path: 'quantity',
          message: t('stockManagement.form.validation.fillAtLeastOneField'),
        });
      }
      return true;
    }
  );

const CustomButton = ({
  formikProps,
  onCancel,
  CreateFaqLoading,
}: {
  formikProps: any;
  onCancel: () => void;
  CreateFaqLoading: boolean;
}) => {
  const { warning } = useThemeColors();
  const { t } = useTranslation();

  const handleFieldChange = (fieldName: string, value: string) => {
    if (value) {
      // Clear other fields
      if (fieldName === 'quantity') {
        formikProps.setFieldValue('substractQuantity', '');
        formikProps.setFieldValue('replaceQuantity', '');
        // Disable and blur other fields
        const substractField = document
          .querySelector('[name="substractQuantity"]')
          ?.closest('.MuiFormControl-root');
        const replaceField = document
          .querySelector('[name="replaceQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (substractField) {
          substractField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          substractField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (replaceField) {
          replaceField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          replaceField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      } else if (fieldName === 'substractQuantity') {
        formikProps.setFieldValue('quantity', '');
        formikProps.setFieldValue('replaceQuantity', '');
        // Disable and blur other fields
        const quantityField = document
          .querySelector('[name="quantity"]')
          ?.closest('.MuiFormControl-root');
        const replaceField = document
          .querySelector('[name="replaceQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (quantityField) {
          quantityField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          quantityField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (replaceField) {
          replaceField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          replaceField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      } else if (fieldName === 'replaceQuantity') {
        formikProps.setFieldValue('quantity', '');
        formikProps.setFieldValue('substractQuantity', '');
        // Disable and blur other fields
        const quantityField = document
          .querySelector('[name="quantity"]')
          ?.closest('.MuiFormControl-root');
        const substractField = document
          .querySelector('[name="substractQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (quantityField) {
          quantityField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          quantityField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (substractField) {
          substractField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          substractField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      }
    } else {
      // Reset all fields when value is cleared
      const fields = document.querySelectorAll('.MuiFormControl-root');
      fields.forEach((field) => {
        field
          .querySelector('.MuiInputBase-root')
          ?.setAttribute(
            'style',
            'opacity: 1; filter: none; pointer-events: auto'
          );
        field
          .querySelector('.MuiFormLabel-root')
          ?.setAttribute('style', 'opacity: 1; filter: none');
      });
    }
    formikProps.setFieldValue(fieldName, value);
  };

  useEffect(() => {
    const { values } = formikProps;
    if (values.quantity) {
      handleFieldChange('quantity', values.quantity);
    } else if (values.substractQuantity) {
      handleFieldChange('substractQuantity', values.substractQuantity);
    } else if (values.replaceQuantity) {
      handleFieldChange('replaceQuantity', values.replaceQuantity);
    } else {
      handleFieldChange('', '');
    }
  }, [formikProps.values]);

  return (
    <Box sx={{ display: 'flex', gap: 2, px: 2 }}>
      <Button
        type="submit"
        variant="contained"
        disabled={
          !formikProps.values.quantity &&
          !formikProps.values.substractQuantity &&
          !formikProps.values.replaceQuantity
        }
        sx={{
          textTransform: 'none',
          fontSize: 14,
          px: 3,
          py: 1,
          backgroundColor: COLORS.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(76, 10, 39, 0.8)',
          },
          '&:disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.26)',
          },
        }}
        onClick={(e) => {
          e.preventDefault();
          formikProps.handleSubmit();
        }}
      >
        {CreateFaqLoading ? (
          <>
            <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
            {t('stockManagement.form.saving')}
          </>
        ) : (
          t('stockManagement.form.save')
        )}
      </Button>
      <Button
        type="button"
        variant="outlined"
        color="error"
        onClick={onCancel}
        sx={{
          bgcolor: warning.main,
          color: '#fff',
          '&:hover': {
            bgcolor: warning.dark,
            color: '#fff',
          },
        }}
      >
        {t('stockManagement.form.cancel')}
      </Button>
    </Box>
  );
};

const AddQuantity = ({ onCancel }: { onCancel: () => void }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const { mutate: editQuantity, isPending: CreateFaqLoading } =
    useEditStockQuantity();
  const stockId = useAtomValue(StockAtom);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  return (
    <>
      {allForm({
        initialValues: {
          quantity: '',
          substractQuantity: '',
          replaceQuantity: '',
        },
        validationSchema: validationSchema(t),
        onSubmit: async (values) => {
          let operation: 'add' | 'subtract' | 'replace';
          let quantity: number;

          if (values.quantity) {
            operation = 'add';
            quantity = parseInt(values.quantity);
          } else if (values.substractQuantity) {
            operation = 'subtract';
            quantity = parseInt(values.substractQuantity);
          } else {
            operation = 'replace';
            quantity = parseInt(values.replaceQuantity);
          }

          const modifiedValues: FaqFormValues = {
            quantity,
            operation,
          };

          try {
            editQuantity(
              {
                id: stockId,
                data: modifiedValues,
              },
              {
                onSuccess: () => {
                  onCancel();
                },
                onError: (error: any) => {
                  console.error('Error editing stock quantity:', error);
                },
              }
            );
          } catch (error) {
            console.error('Error editing stock quantity:', error);}
        },
        fields: [
          {
            name: 'quantity',
            label: t('stockManagement.form.addQuantity'),
            type: 'number',
            placeholder: t('stockManagement.form.placeholders.addQuantity'),
          },
          {
            name: 'substractQuantity',
            label: t('stockManagement.form.subtractQuantity'),
            type: 'number',
            placeholder: t('stockManagement.form.placeholders.subtractQuantity'),
          },
          {
            name: 'replaceQuantity',
            label: t('stockManagement.form.replaceQuantity'),
            type: 'number',
            placeholder: t('stockManagement.form.placeholders.replaceQuantity'),
          },
        ],
        customButton: (formikProps: any) => (
          <CustomButton
            formikProps={formikProps}
            onCancel={onCancel}
            CreateFaqLoading={CreateFaqLoading}
          />
        ),
      })()}
    </>
  );
};

export default AddQuantity;
